import React, { useState, useEffect, useMemo, useRef } from 'react';
import { fetchAllPackages, createPackage, updatePackage, deletePackage, fetchBookingsByPackageId } from '../services/packageService';
import MonthSelector from './MonthSelector';
import AddPackageModal from './AddPackageModal';
import API_BASE_URL from '../config/api';
import { exportPackageBookingsToExcel } from '../utils/excelExport';
import { getTravelOptions } from '../services/travelOptionsService';
import '../styles/UmrahPackagesTable.css';

const PACKAGE_TYPE_LABELS = {
  umrah: 'Umrah',
  explore_dunya: 'Explore Dunya',
};

const UmrahPackagesTable = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedPackageId, setExpandedPackageId] = useState(null);
  const [packageBookings, setPackageBookings] = useState({});
  const [loadingBookings, setLoadingBookings] = useState({});
  const [searchPackageName, setSearchPackageName] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDepartureCity, setFilterDepartureCity] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterPackageType, setFilterPackageType] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [travelOptions, setTravelOptions] = useState(() => getTravelOptions());
  const [newPackageData, setNewPackageData] = useState({
    packageName: '',
    packageType: 'umrah',
    durationFrom: '',
    durationTo: '',
    departureCities: ['Stockholm'], // Array of departure cities
    availableDestinations: '',
    maxBookings: 50,
    status: 'active',
    adult4bed: 20900,
    adult3bed: 22400,
    adult2bed: 23900,
    child4bed: 19900,
    child3bed: 21400,
    child2bed: 22900,
    infant: 5500,
    packageImage: null,
    imagePreview: null,
    hotels: '',
    flights: '',
    breakfastIncluded: false,
    lunchIncluded: false,
    dinnerIncluded: false,
  });
  
  // Use ref to preserve File object across state updates
  const packageImageRef = useRef(null);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${day} ${month}`;
  };

  // Format month with year from duration dates (e.g., "March 2025")
  const formatMonthWithYear = (durationFrom, durationTo) => {
    if (!durationFrom && !durationTo) return '-';
    
    // Use durationFrom if available, otherwise durationTo
    const dateString = durationFrom || durationTo;
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      
      const month = date.toLocaleDateString('en-US', { month: 'long' });
      const year = date.getFullYear();
      return `${month} ${year}`;
    } catch (e) {
      return '-';
    }
  };

  // Extract month name from date for API (e.g., "March" from "2025-03-14")
  const extractMonthFromDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { month: 'long' });
    } catch (e) {
      return '';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('sv-SE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price) + ' SEK';
  };

  const getUploadsBaseUrl = () => {
    // Production environments often set API base with a path (e.g. /api).
    // Uploaded assets are served from /uploads at the server root.
    let baseUrl = (API_BASE_URL || '').trim().replace(/\/+$/, '');
    const apiPath = (process.env.REACT_APP_API_BASE_PATH || '').trim().replace(/\/+$/, '');

    if (apiPath && baseUrl.endsWith(apiPath)) {
      baseUrl = baseUrl.slice(0, -apiPath.length).replace(/\/+$/, '');
    }

    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4).replace(/\/+$/, '');
    }

    return baseUrl || 'http://localhost:5001';
  };

  // Helper function to get package image URL
  const getPackageImageUrl = (imagePath) => {
    if (!imagePath) return null;

    // If backend already returns a fully-qualified URL, use it directly.
    if (/^https?:\/\//i.test(imagePath)) return imagePath;

    const uploadsBaseUrl = getUploadsBaseUrl();
    let path = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;

    // Normalize path separators (handle Windows backslashes)
    path = path.replace(/\\/g, '/');

    // Normalize folder prefixes to avoid duplicated /uploads/uploads...
    if (path.startsWith('uploads/')) {
      path = path.slice('uploads/'.length);
    }

    // Ensure package-images/ prefix for legacy values storing only filename.
    if (!path.startsWith('package-images/')) {
      path = `package-images/${path}`;
    }

    return `${uploadsBaseUrl}/uploads/${path}`;
  };

  // Helper function to format date to YYYY-MM-DD (timezone-safe)
  const formatDateForAPI = (dateValue) => {
    if (!dateValue) return '';
    
    // If already in YYYY-MM-DD format, return as is (most common case from database)
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    
    // If it's an ISO string, extract the date part before 'T'
    if (typeof dateValue === 'string' && dateValue.includes('T')) {
      const datePart = dateValue.split('T')[0];
      // Verify it's in correct format
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return datePart;
      }
    }
    
    // For Date objects, use UTC methods to avoid timezone issues
    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) {
        return '';
      }
      const year = dateValue.getUTCFullYear();
      const month = String(dateValue.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dateValue.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // For other date strings, try to parse and use UTC
    if (typeof dateValue === 'string') {
      // Try to extract YYYY-MM-DD pattern from the string
      const dateMatch = dateValue.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        // Create UTC date to avoid timezone conversion
        const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
        if (!isNaN(date.getTime())) {
          return `${year}-${month}-${day}`;
        }
      }
      
      // Fallback: try parsing as Date and use UTC methods
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }
    
    return dateValue; // Return original if we can't parse it
  };

  const handleEdit = (pkg) => {
    setEditingId(pkg.id);
    // Prepare data for edit modal
    const editPackageData = {
      ...pkg,
      availableDestinations: pkg.availableDestinations || pkg.month || '',
      durationFrom: formatDateForAPI(pkg.durationFrom),
      durationTo: formatDateForAPI(pkg.durationTo),
      // Convert departureCities array or single departureCity to array
      departureCities: pkg.departureCities && Array.isArray(pkg.departureCities) 
        ? pkg.departureCities 
        : (pkg.departureCity ? [pkg.departureCity] : ['Stockholm']),
      // Include new fields
      imagePath: pkg.imagePath,
      currentImageUrl: pkg.imagePath ? getPackageImageUrl(pkg.imagePath) : null,
      hotels: pkg.hotels || '',
      flights: pkg.flights || '',
      breakfastIncluded: pkg.breakfastIncluded || false,
      lunchIncluded: pkg.lunchIncluded || false,
      dinnerIncluded: pkg.dinnerIncluded || false,
      packageType: pkg.packageType || 'umrah',
      packageImage: null, // Reset file input
      imagePreview: null, // Reset preview
      monthSelectorValue: '', // Will be set from duration dates
    };
    setEditData(editPackageData);
  };

  // Fetch packages on component mount
  useEffect(() => {
    const loadPackages = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedPackages = await fetchAllPackages();
        // Format dates from API to YYYY-MM-DD for date inputs and handle departure cities
        const formattedPackages = fetchedPackages.map(pkg => ({
            ...pkg,
            durationFrom: formatDateForAPI(pkg.durationFrom),
            durationTo: formatDateForAPI(pkg.durationTo),
            // Ensure departureCities is an array
            departureCities: pkg.departureCities && Array.isArray(pkg.departureCities) 
              ? pkg.departureCities 
              : (pkg.departureCity ? [pkg.departureCity] : []),
            // Include new fields
            imagePath: pkg.imagePath,
            hotels: pkg.hotels,
            flights: pkg.flights,
            breakfastIncluded: pkg.breakfastIncluded || false,
            lunchIncluded: pkg.lunchIncluded || false,
            dinnerIncluded: pkg.dinnerIncluded || false,
            packageType: pkg.packageType || 'umrah',
          }));
        setPackages(formattedPackages);
      } catch (err) {
        setError(err.message || 'Failed to load packages');
        console.error('Error loading packages:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPackages();
  }, []);

  useEffect(() => {
    const handleTravelOptionsChanged = () => {
      setTravelOptions(getTravelOptions());
    };

    window.addEventListener('travel-options-updated', handleTravelOptionsChanged);
    return () => {
      window.removeEventListener('travel-options-updated', handleTravelOptionsChanged);
    };
  }, []);

  const handleSaveEdit = async () => {
    if (!editingId) return;
    
    // Validate that duration-to is after duration-from
    if (editData.durationFrom && editData.durationTo) {
      const fromDate = new Date(editData.durationFrom);
      const toDate = new Date(editData.durationTo);
      if (toDate <= fromDate) {
        alert('Duration To date must be after Duration From date');
        return;
      }
    }
    
    // Validate that at least one departure city is selected
    const cities = editData.departureCities && Array.isArray(editData.departureCities) 
      ? editData.departureCities 
      : (editData.departureCity ? [editData.departureCity] : []);
    if (!cities || cities.length === 0) {
      alert('Please select at least one departure city');
      return;
    }

    try {
      setSaving(true);
      
      // Use ref to get the File object if state doesn't have it
      const packageImageFile = editData.packageImage instanceof File 
        ? editData.packageImage 
        : (packageImageRef.current instanceof File ? packageImageRef.current : null);
      
      // Prepare data for API (extract month from duration dates and format dates)
      const packageData = {
        ...editData,
        month: extractMonthFromDate(editData.durationFrom) || editData.availableDestinations || editData.month,
        durationFrom: formatDateForAPI(editData.durationFrom),
        durationTo: formatDateForAPI(editData.durationTo),
        // Send departureCities as array
        departureCities: cities,
        // Include packageImage from ref if available, otherwise from state
        ...(packageImageFile && { packageImage: packageImageFile }),
      };

      const updatedPackage = await updatePackage(editingId, packageData);
      
      // Format dates in the updated package response and handle departure cities
      const formattedUpdatedPackage = {
        ...updatedPackage,
        durationFrom: formatDateForAPI(updatedPackage.durationFrom),
        durationTo: formatDateForAPI(updatedPackage.durationTo),
        departureCities: updatedPackage.departureCities && Array.isArray(updatedPackage.departureCities) 
          ? updatedPackage.departureCities 
          : (updatedPackage.departureCity ? [updatedPackage.departureCity] : []),
        // Include new fields
        imagePath: updatedPackage.imagePath,
        hotels: updatedPackage.hotels,
        flights: updatedPackage.flights,
        breakfastIncluded: updatedPackage.breakfastIncluded || false,
        lunchIncluded: updatedPackage.lunchIncluded || false,
        dinnerIncluded: updatedPackage.dinnerIncluded || false,
        packageType: updatedPackage.packageType || 'umrah',
      };
      
      setPackages(packages.map(pkg => 
        pkg.id === editingId ? formattedUpdatedPackage : pkg
      ));
      setEditingId(null);
      setEditData({});
      packageImageRef.current = null;
    } catch (err) {
      alert(`Failed to update package: ${err.message}`);
      console.error('Error updating package:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
    packageImageRef.current = null;
  };

  const handleEditChange = (field, value) => {
    if (field === 'packageImage' && value instanceof File) {
      // Store File object in ref for reliable access
      packageImageRef.current = value;
    } else if (field === 'packageImage' && !value) {
      // Clear ref if image is removed
      packageImageRef.current = null;
    }
    setEditData({ ...editData, [field]: value });
  };

  const handleDeleteConfirm = async () => {
    if (!packageToDelete) return;

    try {
      setDeleting(true);
      await deletePackage(packageToDelete.id);
      
      // Remove package from the list
      setPackages(packages.filter(pkg => pkg.id !== packageToDelete.id));
      
      // Close the confirmation modal
      setPackageToDelete(null);
      
      // If the deleted package was expanded, close it
      if (expandedPackageId === packageToDelete.id) {
        setExpandedPackageId(null);
      }
      
      // Clear bookings for deleted package
      const updatedBookings = { ...packageBookings };
      delete updatedBookings[packageToDelete.id];
      setPackageBookings(updatedBookings);
    } catch (err) {
      alert(`Failed to delete package: ${err.message}`);
      console.error('Error deleting package:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setPackageToDelete(null);
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingId(null);
    setEditData({});
    // Clear the ref
    packageImageRef.current = null;
    // Reset form data
    setNewPackageData({
      packageName: '',
      packageType: 'umrah',
      durationFrom: '',
      durationTo: '',
      departureCities: ['Stockholm'],
      availableDestinations: '',
      monthSelectorValue: '',
      maxBookings: 50,
      status: 'active',
      adult4bed: 20900,
      adult3bed: 22400,
      adult2bed: 23900,
      child4bed: 19900,
      child3bed: 21400,
      child2bed: 22900,
      infant: 5500,
      packageImage: null,
      imagePreview: null,
      hotels: '',
      flights: '',
      breakfastIncluded: false,
      lunchIncluded: false,
      dinnerIncluded: false,
    });
  };

  const handleCloseModal = () => {
    setIsAddingNew(false);
    setNewPackageData({
      packageName: '',
      packageType: 'umrah',
      durationFrom: '',
      durationTo: '',
      departureCities: ['Stockholm'],
      availableDestinations: '',
      monthSelectorValue: '',
      maxBookings: 50,
      status: 'active',
      adult4bed: 20900,
      adult3bed: 22400,
      adult2bed: 23900,
      child4bed: 19900,
      child3bed: 21400,
      child2bed: 22900,
      infant: 5500,
      packageImage: null,
      imagePreview: null,
      hotels: '',
      flights: '',
      breakfastIncluded: false,
      lunchIncluded: false,
      dinnerIncluded: false,
    });
    // Clear the ref when closing
    packageImageRef.current = null;
  };

  const handleSaveNew = async () => {
    if (!newPackageData.packageName || !newPackageData.durationFrom || !newPackageData.durationTo || !newPackageData.availableDestinations) {
      alert('Please fill in all required fields (Package Name, Duration From, Duration To, Month)');
      return;
    }

    // Validate that duration-to is after duration-from
    const fromDate = new Date(newPackageData.durationFrom);
    const toDate = new Date(newPackageData.durationTo);
    if (toDate <= fromDate) {
      alert('Duration To date must be after Duration From date');
      return;
    }

    // Validate that at least one departure city is selected
    const cities = newPackageData.departureCities || (newPackageData.departureCity ? [newPackageData.departureCity] : []);
    if (!cities || cities.length === 0) {
      alert('Please select at least one departure city');
      return;
    }

    try {
      setSaving(true);
      
      // Prepare data for API (extract month from duration dates and format dates)
      // Use ref to get the File object if state doesn't have it
      const packageImageFile = newPackageData.packageImage instanceof File 
        ? newPackageData.packageImage 
        : (packageImageRef.current instanceof File ? packageImageRef.current : null);
      
      const packageData = {
        ...newPackageData,
        month: extractMonthFromDate(newPackageData.durationFrom) || newPackageData.availableDestinations,
        durationFrom: formatDateForAPI(newPackageData.durationFrom),
        durationTo: formatDateForAPI(newPackageData.durationTo),
        // Send departureCities as array
        departureCities: newPackageData.departureCities && Array.isArray(newPackageData.departureCities) 
          ? newPackageData.departureCities 
          : (newPackageData.departureCity ? [newPackageData.departureCity] : []),
        // Include packageImage from ref if available, otherwise from state
        ...(packageImageFile && { packageImage: packageImageFile }),
      };

      const createdPackage = await createPackage(packageData);
      
      // Format dates in the created package response and handle departure cities
      const formattedCreatedPackage = {
        ...createdPackage,
        durationFrom: formatDateForAPI(createdPackage.durationFrom),
        durationTo: formatDateForAPI(createdPackage.durationTo),
        departureCities: createdPackage.departureCities && Array.isArray(createdPackage.departureCities) 
          ? createdPackage.departureCities 
          : (createdPackage.departureCity ? [createdPackage.departureCity] : []),
        packageType: createdPackage.packageType || 'umrah',
      };
      
      setPackages([formattedCreatedPackage, ...packages]);
      setIsAddingNew(false);
      setNewPackageData({
        packageName: '',
        packageType: 'umrah',
        durationFrom: '',
        durationTo: '',
        departureCities: ['Stockholm'],
        availableDestinations: '',
        monthSelectorValue: '',
        maxBookings: 50,
        status: 'active',
        adult4bed: 20900,
        adult3bed: 22400,
        adult2bed: 23900,
        child4bed: 19900,
        child3bed: 21400,
        child2bed: 22900,
        infant: 5500,
        packageImage: null,
        imagePreview: null,
        hotels: '',
        flights: '',
        breakfastIncluded: false,
        lunchIncluded: false,
        dinnerIncluded: false,
      });
    } catch (err) {
      alert(`Failed to create package: ${err.message}`);
      console.error('Error creating package:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleNewPackageChange = (field, value) => {
    if (field === 'packageImage' && value instanceof File) {
      // Store File object in ref for reliable access
      packageImageRef.current = value;
    } else if (field === 'packageImage' && !value) {
      // Clear ref if image is removed
      packageImageRef.current = null;
    }
    // Use functional update to ensure we have the latest state
    setNewPackageData(prevData => ({ ...prevData, [field]: value }));
  };

  const handlePackageRowClick = async (packageId) => {
    // Don't expand if editing
    if (editingId === packageId || isAddingNew) {
      return;
    }

    // Toggle expansion
    if (expandedPackageId === packageId) {
      setExpandedPackageId(null);
      return;
    }

    // Expand and fetch bookings if not already loaded
    setExpandedPackageId(packageId);
    
    if (!packageBookings[packageId]) {
      try {
        setLoadingBookings({ ...loadingBookings, [packageId]: true });
        const bookings = await fetchBookingsByPackageId(packageId);
        setPackageBookings({ ...packageBookings, [packageId]: bookings });
      } catch (error) {
        console.error('Error fetching bookings:', error);
        alert('Failed to load bookings');
      } finally {
        setLoadingBookings({ ...loadingBookings, [packageId]: false });
      }
    }
  };

  const formatBookingDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExportPackageBookings = async (packageId, packageName) => {
    try {
      await exportPackageBookingsToExcel(packageId, packageName);
    } catch (error) {
      console.error('Error exporting package bookings:', error);
      // Error message is already handled in the export function
    }
  };

  const formatDuration = (from, to) => {
    return `${formatDate(from)} - ${formatDate(to)}`;
  };

  // Get unique departure cities from all packages
  const uniqueDepartureCities = useMemo(() => {
    const cities = new Set();
    packages.forEach(pkg => {
      if (pkg.departureCities && Array.isArray(pkg.departureCities)) {
        pkg.departureCities.forEach(city => cities.add(city));
      } else if (pkg.departureCity) {
        cities.add(pkg.departureCity);
      }
    });
    return Array.from(cities).sort();
  }, [packages]);

  // Filter and sort packages
  const filteredAndSortedPackages = useMemo(() => {
    let filtered = [...packages];

    // Filter by package name
    if (searchPackageName) {
      filtered = filtered.filter(pkg =>
        pkg.packageName.toLowerCase().includes(searchPackageName.toLowerCase())
      );
    }

    // Filter by status
    if (filterStatus) {
      filtered = filtered.filter(pkg => pkg.status === filterStatus);
    }

    // Filter by package type (Umrah / Explore Dunya)
    if (filterPackageType) {
      filtered = filtered.filter(pkg => (pkg.packageType || 'umrah') === filterPackageType);
    }

    // Filter by departure city
    if (filterDepartureCity) {
      filtered = filtered.filter(pkg => {
        const cities = pkg.departureCities && Array.isArray(pkg.departureCities)
          ? pkg.departureCities
          : (pkg.departureCity ? [pkg.departureCity] : []);
        return cities.some(city => city === filterDepartureCity);
      });
    }

    // Filter by month
    if (filterMonth) {
      filtered = filtered.filter(pkg => {
        const monthYear = formatMonthWithYear(pkg.durationFrom, pkg.durationTo);
        if (!monthYear || monthYear === '-') return false;
        
        const [selectedYear, selectedMonthNum] = filterMonth.split('-');
        const monthNames = ['january', 'february', 'march', 'april', 'may', 'june',
          'july', 'august', 'september', 'october', 'november', 'december'];
        
        const monthLower = monthYear.toLowerCase();
        const monthIndex = monthNames.findIndex(m => monthLower.includes(m));
        
        if (monthIndex !== -1) {
          const yearMatch = monthYear.match(/\d{4}/);
          if (yearMatch) {
            return yearMatch[0] === selectedYear && (monthIndex + 1) === parseInt(selectedMonthNum);
          }
          return (monthIndex + 1) === parseInt(selectedMonthNum);
        }
        
        return false;
      });
    }

    // Sort
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Handle different data types
        if (sortField === 'durationFrom' || sortField === 'durationTo' || sortField === 'createdAt' || sortField === 'updatedAt') {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        } else if (sortField === 'maxBookings' || sortField === 'availableBookings' || sortField === 'bookedSeats') {
          aValue = parseInt(aValue) || 0;
          bValue = parseInt(bValue) || 0;
        } else if (sortField === 'packageName') {
          aValue = String(aValue || '').toLowerCase();
          bValue = String(bValue || '').toLowerCase();
        } else {
          aValue = String(aValue || '').toLowerCase();
          bValue = String(bValue || '').toLowerCase();
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [packages, searchPackageName, filterStatus, filterPackageType, filterDepartureCity, filterMonth, sortField, sortOrder]);


  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading packages...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  /* Full-page Add/Edit form – replaces table view */
  if (isAddingNew) {
    return (
      <div className="add-package-page-wrapper">
        <AddPackageModal
          isOpen={true}
          onClose={handleCloseModal}
          packageData={newPackageData}
          onChange={handleNewPackageChange}
          onSave={handleSaveNew}
          saving={saving}
          isEditMode={false}
          hotelOptions={travelOptions.hotels}
          flightOptions={travelOptions.flights}
        />
      </div>
    );
  }

  if (editingId !== null) {
    return (
      <div className="add-package-page-wrapper">
        <AddPackageModal
          isOpen={true}
          onClose={handleCancelEdit}
          packageData={editData}
          onChange={handleEditChange}
          onSave={handleSaveEdit}
          saving={saving}
          isEditMode={true}
          hotelOptions={travelOptions.hotels}
          flightOptions={travelOptions.flights}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="table-header-actions">
        <button
          onClick={handleAddNew}
          className="add-package-button"
          disabled={saving}
        >
          + Add New Package
        </button>
      </div>
      
      {/* Search and Filter Section */}
      <div className="packages-table-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="search-package-name">Search by Package Name</label>
            <input
              id="search-package-name"
              type="text"
              placeholder="Enter package name..."
              value={searchPackageName}
              onChange={(e) => setSearchPackageName(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="filter-status">Filter by Status</label>
            <select
              id="filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="coming-soon">Coming Soon</option>
              <option value="sold-out">Sold Out</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="filter-package-type">Filter by Type</label>
            <select
              id="filter-package-type"
              value={filterPackageType}
              onChange={(e) => setFilterPackageType(e.target.value)}
              className="filter-select"
            >
              <option value="">All Types</option>
              <option value="umrah">{PACKAGE_TYPE_LABELS.umrah}</option>
              <option value="explore_dunya">{PACKAGE_TYPE_LABELS.explore_dunya}</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="filter-departure-city">Filter by Departure City</label>
            <select
              id="filter-departure-city"
              value={filterDepartureCity}
              onChange={(e) => setFilterDepartureCity(e.target.value)}
              className="filter-select"
            >
              <option value="">All Cities</option>
              {uniqueDepartureCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <MonthSelector
              id="filter-month"
              label="Filter by Month"
              value={filterMonth}
              onChange={setFilterMonth}
              placeholder="Select Month"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="sort-select">Sort By</label>
            <select
              id="sort-select"
              value={`${sortField}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortField(field);
                setSortOrder(order);
              }}
              className="filter-select"
            >
              <option value="">No Sorting</option>
              <option value="createdAt-desc">Created At (Most Recent First)</option>
              <option value="createdAt-asc">Created At (Least Recent First)</option>
              <option value="packageName-asc">Package Name (A-Z)</option>
              <option value="packageName-desc">Package Name (Z-A)</option>
              <option value="durationFrom-desc">Start Date (Newest First)</option>
              <option value="durationFrom-asc">Start Date (Oldest First)</option>
              <option value="maxBookings-desc">Max Bookings (High to Low)</option>
              <option value="maxBookings-asc">Max Bookings (Low to High)</option>
              <option value="availableBookings-desc">Available (High to Low)</option>
              <option value="availableBookings-asc">Available (Low to High)</option>
            </select>
          </div>
          {(searchPackageName || filterStatus || filterPackageType || filterDepartureCity || filterMonth) && (
            <div className="filter-group">
              <button
                type="button"
                onClick={() => {
                  setSearchPackageName('');
                  setFilterStatus('');
                  setFilterPackageType('');
                  setFilterDepartureCity('');
                  setFilterMonth('');
                }}
                className="filter-clear-btn"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="umrah-packages-table-container">
        <table className="umrah-packages-table">
        <thead>
          <tr>
            <th>Image</th>
            <th>Package Name</th>
            <th>Type</th>
            <th>Duration Period</th>
            <th>Departure Cities</th>
            <th>Month</th>
            <th>Max Bookings</th>
            <th>Available Bookings(#)</th>
            <th>Status</th>
            <th>Hotels</th>
            <th>Flights</th>
            <th>Meals</th>
            <th>Adult: 4-bed</th>
            <th>Adult: 3-bed</th>
            <th>Adult: 2-bed</th>
            <th>Children: 4-bed</th>
            <th>Children: 3-bed</th>
            <th>Children: 2-bed</th>
            <th>Infants</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedPackages.length === 0 ? (
            <tr>
              <td colSpan="20" className="no-results">
                No packages match your filters.
              </td>
            </tr>
          ) : (
            filteredAndSortedPackages.map((pkg) => (
            <React.Fragment key={pkg.id}>
              <tr 
                className={expandedPackageId === pkg.id ? 'package-row-expanded' : ''}
                onClick={() => handlePackageRowClick(pkg.id)}
                style={{ cursor: isAddingNew ? 'default' : 'pointer' }}
              >
                <>
                    <td>
                      {pkg.imagePath ? (
                        <img 
                          src={getPackageImageUrl(pkg.imagePath)} 
                          alt={pkg.packageName}
                          style={{ 
                            width: '60px', 
                            height: '60px', 
                            objectFit: 'cover', 
                            borderRadius: '6px',
                            border: '1px solid #d1d5db'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'block';
                          }}
                        />
                      ) : (
                        <div style={{ 
                          width: '60px', 
                          height: '60px', 
                          backgroundColor: '#f3f4f6', 
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          color: '#9ca3af',
                          textAlign: 'center',
                          padding: '4px'
                        }}>
                          No Image
                        </div>
                      )}
                      <span style={{ display: 'none', fontSize: '10px', color: '#9ca3af' }}>Image not available</span>
                    </td>
                    <td className="package-name-cell">{pkg.packageName}</td>
                    <td>{PACKAGE_TYPE_LABELS[pkg.packageType] || pkg.packageType || 'Umrah'}</td>
                    <td>{formatDuration(pkg.durationFrom, pkg.durationTo)}</td>
                    <td>
                      {pkg.departureCities && Array.isArray(pkg.departureCities) && pkg.departureCities.length > 0
                        ? pkg.departureCities.join(', ')
                        : (pkg.departureCity || '-')}
                    </td>
                    <td>{formatMonthWithYear(pkg.durationFrom, pkg.durationTo)}</td>
                    <td>{pkg.maxBookings}</td>
                    <td className="available-bookings-cell">
                      <span className={pkg.availableBookings === 0 ? 'available-zero' : ''}>
                        {pkg.availableBookings !== undefined ? pkg.availableBookings : pkg.maxBookings}
                      </span>
                      {pkg.bookedSeats !== undefined && (
                        <span className="booked-seats-info">
                          ({pkg.bookedSeats} booked)
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge status-${pkg.status}`}>
                        {pkg.status === 'coming-soon' ? 'Coming Soon' : 
                         pkg.status === 'sold-out' ? 'Sold Out' : 
                         pkg.status}
                      </span>
                    </td>
                    <td>
                      {pkg.hotels ? (
                        <div style={{ 
                          maxWidth: '200px', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          title: pkg.hotels
                        }}>
                          {pkg.hotels}
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>-</span>
                      )}
                    </td>
                    <td>
                      {pkg.flights ? (
                        <div style={{ 
                          maxWidth: '200px', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          title: pkg.flights
                        }}>
                          {pkg.flights}
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>-</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {pkg.breakfastIncluded && (
                          <span style={{ 
                            padding: '2px 8px', 
                            backgroundColor: '#d1fae5', 
                            color: '#065f46', 
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            B
                          </span>
                        )}
                        {pkg.lunchIncluded && (
                          <span style={{ 
                            padding: '2px 8px', 
                            backgroundColor: '#dbeafe', 
                            color: '#1e40af', 
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            L
                          </span>
                        )}
                        {pkg.dinnerIncluded && (
                          <span style={{ 
                            padding: '2px 8px', 
                            backgroundColor: '#fce7f3', 
                            color: '#831843', 
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            D
                          </span>
                        )}
                        {!pkg.breakfastIncluded && !pkg.lunchIncluded && !pkg.dinnerIncluded && (
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>
                        )}
                      </div>
                    </td>
                    <td className="price-cell">{formatPrice(pkg.adult4bed)}</td>
                    <td className="price-cell">{formatPrice(pkg.adult3bed)}</td>
                    <td className="price-cell">{formatPrice(pkg.adult2bed)}</td>
                    <td className="price-cell">{formatPrice(pkg.child4bed)}</td>
                    <td className="price-cell">{formatPrice(pkg.child3bed)}</td>
                    <td className="price-cell">{formatPrice(pkg.child2bed)}</td>
                    <td className="price-cell">{formatPrice(pkg.infant)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="icon-button-wrapper">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(pkg);
                            }}
                            disabled={isAddingNew || editingId !== null || saving || deleting}
                            style={{
                              padding: '8px',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '36px',
                              height: '36px',
                              transition: 'background-color 0.2s',
                              opacity: (isAddingNew || editingId !== null || saving || deleting) ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => {
                              if (!e.currentTarget.disabled) {
                                e.currentTarget.style.backgroundColor = '#2563eb';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!e.currentTarget.disabled) {
                                e.currentTarget.style.backgroundColor = '#3b82f6';
                              }
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          <span className="icon-tooltip">Edit</span>
                        </div>
                        <div className="icon-button-wrapper">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportPackageBookings(pkg.id, pkg.packageName);
                            }}
                            disabled={isAddingNew || editingId !== null || saving || deleting}
                            style={{
                              padding: '8px',
                              backgroundColor: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '36px',
                              height: '36px',
                              transition: 'background-color 0.2s',
                              opacity: (isAddingNew || editingId !== null || saving || deleting) ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => {
                              if (!e.currentTarget.disabled) {
                                e.currentTarget.style.backgroundColor = '#059669';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!e.currentTarget.disabled) {
                                e.currentTarget.style.backgroundColor = '#10b981';
                              }
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="7 10 12 15 17 10"></polyline>
                              <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                          </button>
                          <span className="icon-tooltip">Download</span>
                        </div>
                        <div className="icon-button-wrapper">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPackageToDelete(pkg);
                            }}
                            disabled={isAddingNew || editingId !== null || saving || deleting}
                            style={{
                              padding: '8px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '36px',
                              height: '36px',
                              transition: 'background-color 0.2s',
                              opacity: (isAddingNew || editingId !== null || saving || deleting) ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => {
                              if (!e.currentTarget.disabled) {
                                e.currentTarget.style.backgroundColor = '#dc2626';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!e.currentTarget.disabled) {
                                e.currentTarget.style.backgroundColor = '#ef4444';
                              }
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          </button>
                          <span className="icon-tooltip">Delete</span>
                        </div>
                      </div>
                    </td>
                  </>
              </tr>
              {expandedPackageId === pkg.id && (
                <tr className="bookings-detail-row">
                  <td colSpan="20" className="bookings-detail-cell">
                    {loadingBookings[pkg.id] ? (
                      <div className="loading-bookings">Loading bookings...</div>
                    ) : (
                      <div className="bookings-table-container">
                        <h3 className="bookings-table-title">Bookings for {pkg.packageName}</h3>
                        {packageBookings[pkg.id] && packageBookings[pkg.id].length > 0 ? (
                          <table className="bookings-table">
                            <thead>
                              <tr>
                                <th>Reference</th>
                                <th>Passenger Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Tickets</th>
                                <th>Total Price</th>
                                <th>Status</th>
                                <th>Payment Status</th>
                                <th>Created At</th>
                              </tr>
                            </thead>
                            <tbody>
                              {packageBookings[pkg.id].map((booking) => (
                                <tr key={booking.id}>
                                  <td>{booking.bookingReference}</td>
                                  <td>
                                    {booking.passengers && booking.passengers.length > 0
                                      ? booking.passengers.map(p => p.full_name).join(', ')
                                      : '-'}
                                  </td>
                                  <td>
                                    {booking.passengers && booking.passengers.length > 0
                                      ? booking.passengers[0].email
                                      : '-'}
                                  </td>
                                  <td>
                                    {booking.passengers && booking.passengers.length > 0
                                      ? booking.passengers[0].phone_number
                                      : '-'}
                                  </td>
                                  <td>
                                    {booking.tickets ? (
                                      <div className="ticket-breakdown">
                                        {booking.tickets.adult4bed > 0 && <span>A4: {booking.tickets.adult4bed} </span>}
                                        {booking.tickets.adult3bed > 0 && <span>A3: {booking.tickets.adult3bed} </span>}
                                        {booking.tickets.adult2bed > 0 && <span>A2: {booking.tickets.adult2bed} </span>}
                                        {booking.tickets.child4bed > 0 && <span>C4: {booking.tickets.child4bed} </span>}
                                        {booking.tickets.child3bed > 0 && <span>C3: {booking.tickets.child3bed} </span>}
                                        {booking.tickets.child2bed > 0 && <span>C2: {booking.tickets.child2bed} </span>}
                                        {booking.tickets.infant > 0 && <span>I: {booking.tickets.infant} </span>}
                                        <span className="total-tickets">(Total: {booking.tickets.totalTickets})</span>
                                      </div>
                                    ) : '-'}
                                  </td>
                                  <td className="price-cell">{formatPrice(booking.totalPrice)}</td>
                                  <td>
                                    <span className={`status-badge status-${booking.status}`}>
                                      {booking.status}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`status-badge status-${booking.paymentStatus}`}>
                                      {booking.paymentStatus}
                                    </span>
                                  </td>
                                  <td>{formatBookingDate(booking.createdAt)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="no-bookings">No bookings found for this package.</div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
            ))
          )}
        </tbody>
      </table>
      </div>

      {/* Delete Confirmation Modal */}
      {packageToDelete && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Package</h2>
              <button className="close-button" onClick={handleDeleteCancel}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '30px' }}>
              <p style={{ marginBottom: '20px', fontSize: '16px', color: '#374151' }}>
                Are you sure you want to delete the package <strong>"{packageToDelete.packageName}"</strong>?
              </p>
              <p style={{ marginBottom: '20px', fontSize: '14px', color: '#6b7280' }}>
                This action cannot be undone. All associated data will be permanently deleted.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '30px' }}>
                <button
                  onClick={handleDeleteCancel}
                  className="cancel-button-modal"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="delete-button-modal"
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Package'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UmrahPackagesTable;
